import React, { useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { House, Clock, CalendarCheck, FolderOpen } from '@phosphor-icons/react';

/**
 * Mobile-only bottom navigation for the Employee Self-Service portal.
 * Renders as a fixed strip on screens < lg (1024px) and stays hidden
 * on desktop where the top-page BackButton + role dashboards handle
 * navigation. Visibility scope:
 *  – Only signed-in users
 *  – Only on `/employee/*` and `/home` routes
 *  – Admin users see it too (they have personal ESS cards on /home)
 *
 * Includes iOS safe-area padding via `env(safe-area-inset-bottom)`
 * so tab labels don't clash with the home indicator on iPhones.
 */
export default function BottomNav() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const location = useLocation();

  // Tag the <html> element so pages can add `pb-24 lg:pb-0` via the
  // global rule in App.css when the bar is on-screen. Cleaner than
  // patching every ESS page individually.
  const path = location.pathname || '';
  // Pure system-admin accounts have no personal attendance/leaves to
  // manage (they're a service account, not an employee), so hiding the
  // bar for them avoids a confusing "حضوري/إجازاتي" strip on a dashboard
  // that doesn't own any such data. GMs, department managers, finance
  // and employees all remain employees-of-record and keep the bar.
  const isSystemAdmin =
    user?.role === 'admin' &&
    !user?.is_general_manager &&
    !user?.is_finance_manager &&
    !user?.is_department_manager;
  const visible = !!user && !isSystemAdmin && (path.startsWith('/employee') || path === '/home');
  useEffect(() => {
    const cls = 'has-bottom-nav';
    if (visible) document.documentElement.classList.add(cls);
    else document.documentElement.classList.remove(cls);
    return () => document.documentElement.classList.remove(cls);
  }, [visible]);

  if (!visible) return null;

  const items = [
    { path: '/home', icon: House, label: t('dashboard') },
    { path: '/employee/attendance', icon: Clock, label: t('my_attendance') },
    { path: '/employee/leaves', icon: CalendarCheck, label: t('my_leaves') },
    { path: '/employee/my-documents', icon: FolderOpen, label: t('my_documents') },
  ];

  return (
    <nav
      className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl border-t border-[#DDD6FE] dark:border-slate-700 shadow-[0_-4px_20px_-8px_rgba(124,58,237,0.15)]"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      data-testid="mobile-bottom-nav"
    >
      <div className="flex items-stretch justify-around px-1">
        {items.map((it) => (
          <NavLink
            key={it.path}
            to={it.path}
            className={({ isActive }) =>
              `flex-1 flex flex-col items-center justify-center gap-1 py-2 min-h-[56px] text-[11px] font-medium transition-colors ${
                isActive
                  ? 'text-[#7C3AED] dark:text-violet-300'
                  : 'text-[#5B21B6]/60 dark:text-slate-400 hover:text-[#7C3AED] dark:hover:text-violet-300'
              }`
            }
            data-testid={`bottom-nav-${it.path.split('/').pop() || 'home'}`}
          >
            {({ isActive }) => (
              <>
                <it.icon
                  size={22}
                  weight={isActive ? 'fill' : 'duotone'}
                  className="shrink-0"
                />
                <span className="leading-none">{it.label}</span>
              </>
            )}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}
